<template>
  <div v-if="build" class="Deposit center-xs">
    <router-link :to="{ name: 'FeatureSelect', params: storeId }">
      <div class="back-to-home">
        <v-icon>chevron_left</v-icon>
        <span>
          {{ $vuetify.lang.t("$vuetify.deposit.backToHome") }}
        </span>
      </div>
    </router-link>
    <spacer size="xl" />
    <spacer size="m" />
    <div class="fs-xl fw-bold">
      {{ $vuetify.lang.t("$vuetify.deposit.card1.title") }}
    </div>
    <spacer size="s" />
    <div class="fs-s">
      {{ $vuetify.lang.t("$vuetify.deposit.card1.subtitle") }}
      {{ getBonusTip }}
    </div>
    <spacer size="xl" />
    <spacer size="m" />

    <template v-if="promotions.length > 0">
      <spacer size="m" />
      <div class="gc-promotions-box">
        <div class="gc-promotions-title">
          {{ $vuetify.lang.t("$vuetify.giftcard.currentPromotions") }}
        </div>
        <div
          v-for="(promo, index) in promotions"
          :key="index"
          class="gc-promo-item"
          v-html="promo"
        >
        </div>
      </div>
      <spacer size="m" />
    </template>

    <form class="start-xs FormContainer">
      <preset-row
        v-if="build.presetAmountsInCents && build.presetAmountsInCents.length"
        ref="amount"
        :items="build.presetAmountsInCents"
        :amount.sync="depositDetails.amount"
        :filter="toDollars"
        :showOther="showOther"
      />
      <masked-input
        v-else
        :label="$vuetify.lang.t('$vuetify.forms.amountPrompt')"
        icon="attach_money"
        :raw.sync="depositDetails.amount"
        validation="nonempty"
        :errorMessages="errors['nameError']"
      />
      <spacer size="m" />

      <div class="fs-l fw-bold">
        {{ $vuetify.lang.t("$vuetify.deposit.card1.memberInformation") }}
      </div>
      <spacer size="s" border-bottom />
      <spacer size="s" />
      <div class="fs-s">
        {{
          $vuetify.lang.t("$vuetify.deposit.card1.memberInformationSubtitle")
        }}
      </div>
      <spacer size="m" />

      <masked-input
        ref="phone"
        :label="$vuetify.lang.t('$vuetify.forms.deposit.phone')"
        :hint="$vuetify.lang.t('$vuetify.forms.deposit.phoneHint')"
        icon="smartphone"
        mask="phoneNumber"
        validation="validatePhone"
        :errorMessages="errors['phoneError']"
        :raw.sync="depositDetails.telephone"
        required
        limitRawLength="10"
        :disabled="!!$parent.$parent.returnUrl && !!authorizationId"
      />
    </form>

    <!-- Was two stacked <spacer size="xl" /> (64px combined, no reason for
         two) -- that's the "random empty div" showing up in devtools; one is
         enough gap above the button. margin-bottom gives it breathing room
         from the footer below, which it had none of before. -->
    <spacer size="xl" />

    <v-btn
      min-width="250"
      style="padding:0 40px; text-transform: none; margin-bottom: 10px;"
      rounded
      color="primary"
      :style="{ color: btnTextColor }"
      :disabled="!readyToProceed"
      @click="moveToCheckout"
    >
      <img src="/giftcard/img/wallet-black.png" :style="{ marginRight: '10px', width: '24px', height: '24px', filter: walletIconFilter }" />
      {{ $vuetify.lang.t("$vuetify.deposit.checkoutAndDeposit") }}
    </v-btn>
  </div>
</template>

<script>
import maskedInput from "../common/maskedInput";
import PresetRow from "../common/PresetRow";
import { mapMutations, mapActions, mapGetters } from "vuex";
import Spacer from "../common/Spacer";
import { get } from "lodash";
import { primaryTextColor } from "@/plugins/util";
export default {
  components: {
    maskedInput,
    PresetRow,
    Spacer,
  },

  data: () => ({
    errors: {},
  }),

  computed: {
    btnTextColor() { return primaryTextColor(this.$store.getters.getManifest?.meta?.primary) },
    walletIconFilter() {
      if (!this.readyToProceed) {
        // recolor black PNG to #AEAEAE
        return 'brightness(0) saturate(100%) invert(74%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(86%)'
      }
      if (primaryTextColor(this.$store.getters.getManifest?.meta?.primary) === '#ffffff') {
        // recolor black PNG to white
        return 'brightness(0) invert(1)'
      }
      return 'none' // keep black
    },
    infoEntered() {
      return !!this.depositDetails.telephone && !!this.depositDetails.amount;
    },
    readyToProceed() {
      return this.infoEntered && !Object.keys(this.errors).length;
    },

    showOther() {
      return !!(
        this.customization && this.customization.allowCustomizedDepositAmount
      );
    },
    getBonusTip() {
      const bonuses = get(this.customization, "bonuses", []);
      if (!Array.isArray(bonuses) || bonuses.length === 0) {
        return null;
      }

      const validBonusItems = bonuses.filter((bonusItem) => {
        const isValidRange =
          bonusItem.amountDepositMax >= bonusItem.amountDepositMin;
        const isValidBonus = bonusItem.bonusCash > 0;
        return isValidRange && isValidBonus;
      });

      // Return null directly if there are no valid bonus items
      if (validBonusItems.length === 0) {
        return null;
      }

      // Sort the valid bonus items from low to high by deposit range (based on amountDepositMin)
      // This ensures we verify if the bonus is increasing in the order of "lower deposit to higher deposit"
      const sortedBonusItems = validBonusItems.sort((a, b) => {
        return a.amountDepositMin - b.amountDepositMin;
      });

      // Verify the positive correlation (higher deposit corresponds to higher bonusCash)
      let isHigherDepositWithHigherBonus = true;
      for (let i = 1; i < sortedBonusItems.length; i++) {
        const prevItem = sortedBonusItems[i - 1]; // Previous item (lower deposit range)
        const currItem = sortedBonusItems[i]; // Current item (higher deposit range)

        // Core judgment: If the bonusCash of the current (higher deposit range) is less than or equal to
        // that of the previous (lower deposit range), the condition is not satisfied
        if (currItem.bonusCash <= prevItem.bonusCash) {
          isHigherDepositWithHigherBonus = false;
          break; // Terminate the loop immediately once an unsatisfied condition is found
        }
      }

      return isHigherDepositWithHigherBonus
        ? this.$vuetify.lang.t("$vuetify.deposit.card1.bonusTip")
        : null;
    },

    promotions() {
      const bonuses = get(this.customization, "bonuses", []);
      const promotionsBonuses = bonuses
        .filter((bonus) => bonus.bonusCash > 0)
        .map((bonus) => {
          const { bonusCash, amountDepositMin, amountDepositMax } = bonus;
          if (amountDepositMax === amountDepositMin) {
            return this.$vuetify.lang.t(
              "$vuetify.deposit.card2.bonusInfo",
              this.currency(bonusCash),
              this.currency(amountDepositMin)
            );
          } else if (amountDepositMax === -1) {
            return this.$vuetify.lang.t(
              "$vuetify.deposit.card2.bonusInfoOrMore",
              this.currency(bonusCash),
              this.currency(amountDepositMin)
            );
          }
          return this.$vuetify.lang.t(
            "$vuetify.deposit.card2.bonusInfoRange",
            this.currency(bonusCash),
            this.currency(amountDepositMin),
            this.currency(amountDepositMax)
          );
        });

      const promotionsPoints = bonuses
        .filter((bonus) => bonus.bonusPoints > 0)
        .map((bonus) => {
          const { bonusPoints, amountDepositMin, amountDepositMax } = bonus;
          if (amountDepositMax === amountDepositMin) {
            return this.$vuetify.lang.t(
              "$vuetify.deposit.card2.bonusInfoPoints",
              bonusPoints,
              this.currency(amountDepositMin)
            );
          } else if (amountDepositMax === -1) {
            return this.$vuetify.lang.t(
              "$vuetify.deposit.card2.bonusInfoPointsOrMore",
              bonusPoints,
              this.currency(amountDepositMin)
            );
          }
          return this.$vuetify.lang.t(
            "$vuetify.deposit.card2.bonusInfoPointsRange",
            bonusPoints,
            this.currency(amountDepositMin),
            this.currency(amountDepositMax)
          );
        });
      return [...promotionsBonuses, ...promotionsPoints];
    },

    ...mapGetters({
      storeId: "getStoreID",
      build: "getBuild",
      depositDetails: "getDepositInfo",
      currencySymbol: "getCurrenySymbol",
      customization: "getDepositCustomization",
      authorizationId: "getAuthId",
    }),
  },

  methods: {
    toDollars: (value) => value / 100,
    currency(val, showDec = false) {
      return this.$options.filters.currency(val, showDec);
    },
    moveToCheckout() {
      this.setDepositDetails(this.depositDetails);

      this.$router.push({
        name: "Checkout",
        params: { ...this.storeId },
      });
    },

    checkValidation() {
      for (const ref in this.$refs) {
        // Run the validation method of each input
        if (ref && this.$refs[ref] && "validate" in this.$refs[ref])
          this.$refs[ref].validate();
      }
    },
    ...mapMutations(["setBuild", "setDepositDetails"]),
    ...mapActions(["fetchDepositStore"]),
  },
};
</script>

<style scoped>
h2 {
  font-size: 18px;
  font-weight: 600;

  margin: 0px 0px 10px;
}

.back-to-home {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
  line-height: 24px;
  font-weight: 600;
}

.v-card__text {
  font-size: 16px;
}

.introduction {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 20px;
}
.CouponCard {
  text-align: left;
  padding: 10px 10px 20px 10px;
  margin: 0px;

  border-color: var(--v-primary-base) !important;
  border: solid;
  border-width: 2px 2px 2px 10px;
  border-radius: 5px;

  display: inline-block;
  position: relative;

  height: 100%;
  width: 100%;

  white-space: normal;
  overflow: hidden;
}
.label {
  font-size: 16px;
  font-weight: 700;
}
.header {
  font-size: 16px;
  font-weight: 600;
  color: #4caf50;
}
.subheader {
  font-size: 14px;
  font-weight: 400;
  color: grey;

  margin: 10px 0px;
}
.quantity {
  position: absolute;
  bottom: 5px;

  color: grey;
}

.highlight {
  border-color: var(--v-primary-base) !important;
  border: solid;
  border-width: 2px;
}

.value {
  text-align: right;
  float: right;
}
</style>
<style>
.v-data-table__selected,
	.v-data-table__selected td,
	.v-data-table__selected li,
	/* .v-data-table__selected span, */
	.v-data-table__selected div {
  background-color: var(--selected-row-bg-color) !important;
  color: white !important;
}
.v-data-table__selected span,
.v-data-table__selected b {
  color: white !important;
}
th {
  text-align: left;
}
.v-data-table__expanded__content td {
  padding: 20px !important;
}

table.v-table {
  max-width: none !important;
}

.v-data-table .v-btn {
  height: auto !important;
  padding: 5px 10px !important;
}
.v-dialog .v-card,
.bonusOptions .v-card {
  margin: 0px !important;
}

.v-data-table__mobile-row {
  position: relative;
}
.couponsCount {
  /* position: absolute;
		left: 130px;
		top: calc(50% - 10px); */
}
.gc-promotions-box {
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #d9d9d9;
  text-align: left;
}

.gc-promotions-title {
  font-size: 17px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 8px;
}

.gc-promo-item {
  font-size: 17px;
  line-height: 24px;
  color: var(--primary-color, #16a34a);
  margin-bottom: 8px;
}

.gc-promo-item:last-child {
  margin-bottom: 0;
}
</style>
